import React, { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete, {
  AutocompleteChangeReason,
} from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { getFilterMasterUser } from "../../API/getFilterMasterUser";
import {
  APP,
  EAutoSearchFieldName,
  EXCEPTION_HEADERS_LIST,
  ETab,
  TMasterSearchParameter,
} from "../../constants";
import { exceptionAssign } from "../../API/exceptionAssign";
import { useDispatch, useSelector } from "react-redux";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { debounce } from "lodash";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { determineTab, noOptionsText } from "../../helpers";
import { IMaterUser } from "../../models/user";
import AppState from "../../AppState";

type Props = {
  selectedOSDIds?: GridRowSelectionModel | number[];
  isOpen?: boolean;
  onClose?: () => void;
  onSuccessFullyAssigned?: () => void;
};

const AssignTo: React.FC<Props> = (props: Props): JSX.Element => {
  const [assignUserList, setAssignUserList] = React.useState<IMaterUser[]>([]);
  const [assignUserObject, setAssignUserObject] =
    React.useState<IMaterUser | null>(null);
  const [assignUser, setAssignUser] = React.useState<string>("");
  const [pageLoading, setPageLoading] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  const tab = determineTab(globalThis.location.pathname);

   const { activeTerminal } =
      useSelector((state: AppState) => ({
        activeTerminal: state.activeTerminal,
      }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((values) => getFiltersAssignUserDataAPI(values), 1000),
    []
  );

  const onInputChange = (event: React.SyntheticEvent, values: string) => {
    if (values === "undefined") return;
    setAssignUser(values);
    setLoading(true);
    setAssignUserList([]);
    debouncedSave(values);
  };

  const onAssignUserChanged = (
    event: React.SyntheticEvent,
    value: any,
    reason?: AutocompleteChangeReason
  ) => {
    if (reason === "clear") {
      setAssignUserList([]);
      setAssignUserObject(null);
    }
    if (!value || value === "undefined") return;
    setAssignUserObject(value);
  };

  const dispatch = useDispatch();
  const assignToUser = () => {
    if(pageLoading) return;
    setPageLoading(true);
      const headers = EXCEPTION_HEADERS_LIST[tab as keyof typeof EXCEPTION_HEADERS_LIST] || {};

      const terminalId = activeTerminal?.terminalId ?? "";

      if (terminalId) {
      if ([ETab.overages].includes(tab)) {
        headers["OSD-Reporting-Terminal"] = terminalId;
      } else if ([ETab.misdelivery].includes(tab)) {
        headers["OSD-Pickup-Terminal"] = terminalId;
      } else if (![ETab.myAssignments, ETab.dashboard].includes(tab)) {
        headers["OSD-Destination-Terminal"] =  terminalId;
      }
    }
    
    exceptionAssign({
      osdIds: props.selectedOSDIds,
      username: assignUserObject?.as400Id,
    }, headers)
      .then((responseData: { status: string }) => {
        setPageLoading(false);
        if (props.onSuccessFullyAssigned) props.onSuccessFullyAssigned();
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.SUCCESS,
            "OSD records assigned successfully.",
            Date.now().toString()
          )
        );
      })
      .catch((err) => {
        setPageLoading(false);
        if (props.onSuccessFullyAssigned) {
          props.onSuccessFullyAssigned();
        }
        dispatch(
          PopoutNotification(
            typeof err === "string" &&
              err.toLocaleLowerCase().includes("missing email address")
              ? NOTIFICATION_TYPE.WARNING
              : NOTIFICATION_TYPE.ERROR,
            typeof err === "string"
              ? err
              : JSON.stringify(err).substring(0, 100),
            Date.now().toString()
          )
        );
      });
  };

    const handleFilterNameOption = (option: string | IMaterUser): string => {
      if (typeof option === "string") return option;
      const { firstName = "", lastName = "", nameFullLc = "" } = option;
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      return fullName || nameFullLc;
    };

  const getFiltersAssignUserDataAPI = (values: string) => {
    if (values?.length > 1) {
      let params: TMasterSearchParameter = {
        search: values.toString(),
        page: APP.overage.userAPIpageLimit,
        limit: APP.overage.userAPILimit,
        includeInactiveUsers: false,
      };
      getFilterMasterUser(params)
        .then((users: IMaterUser[]) => {
          setLoading(false);
          setAssignUserList(users);
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
        });
    }
  };
  return (
    <div>
      <Dialog
        open={props.isOpen!}
        aria-labelledby="customized-dialog-title"
        keepMounted
        maxWidth="sm"
        fullWidth={true}
        className="assignment"
        data-testid="assign-dialog"
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
          className="modal-header"
          data-testid="dialog-title"
        >
          Assign Exception
        </DialogTitle>
        <IconButton
          aria-label="close"
          className={pageLoading ? "disable-btn" : ""}
          onClick={pageLoading ? void 0 : props.onClose}
          sx={{
            position: "absolute",
            right: 15,
            top: 15,
            color: (theme: any) => theme.palette.grey[500],
          }}
          data-testid="close-button"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers className="assignment-area" data-testid="dialog-content">
          <Grid item md={3} xs={9}>
            <FormControl fullWidth size="small">
              <InputLabel
                style={{ fontSize: "13px" }}
                shrink={true}
                id="assignedTo"
              >
                Select User <sup>*</sup>
              </InputLabel>
              <Autocomplete
                disablePortal
                size="small"
                id="assignedTo"
                disabled={pageLoading}
                value={assignUserObject}
                onInputChange={onInputChange}
                onChange={onAssignUserChanged}
                inputValue={assignUser}
                noOptionsText={noOptionsText(
                  assignUser,
                  loading,
                  EAutoSearchFieldName.name
                )}
                forcePopupIcon={false}
                className="autocomplete"
                options={assignUserList}
                getOptionLabel={handleFilterNameOption}
                isOptionEqualToValue={(option, value) =>
                  option?.nameFullLc === value?.nameFullLc
                }
                filterOptions={(options) => options}
                renderInput={(params) => <TextField  autoComplete="off" {...params} data-testid="assign-user-input" />}
              />
            </FormControl>
              <Button
                onClick={assignUserObject ?  assignToUser : void 0}
                variant="contained"
                 data-testid="assign-button"
                className={
                  assignUserObject && !pageLoading ?  "assign" : " disable disable-btn assign"
                }
              >
                Assign
              </Button>

            <Button
              onClick={pageLoading ? void 0 : props.onClose}
              variant="contained"
              data-testid="cancel-button"
              className={pageLoading ? "disable-btn disable" : "disable"}
            >
              Cancel
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignTo;
